import type { AnimalItem, ShareItem } from "../mocks/animals";
import type { CustomerItem } from "../mocks/customers";
import type { PaymentRecord } from "../mocks/payments";
import type { DeliveryAssignment } from "./deliveryStore";
import { recalcAllAnimals, recalcCustomersFromAnimals } from "./cascadeOps";
import { getDisplayKupeNo, getDisplaySiparisNo } from "./animalLabels";
import { buildGroupedHisseNo, getShareSlotNos, getShareSortKey } from "./salesFlow";

function uniqueSorted(values: number[]) {
  return Array.from(new Set(values))
    .filter((value) => Number.isFinite(value) && value > 0)
    .sort((a, b) => a - b);
}

export function getOccupiedSlotNos(animal: AnimalItem) {
  return uniqueSorted(
    (animal.hisseler ?? []).flatMap((share) => getShareSlotNos(share))
  );
}

export function getAvailableSlotNos(animal: AnimalItem) {
  const occupied = new Set(getOccupiedSlotNos(animal));
  const result: number[] = [];

  for (let i = 1; i <= Math.max(0, animal.toplamHisse ?? 0); i += 1) {
    if (!occupied.has(i)) {
      result.push(i);
    }
  }

  return result;
}

export function canTransferShareToAnimal(share: ShareItem, animal: AnimalItem) {
  return getAvailableSlotNos(animal).length >= Math.max(1, share.hisseSayisi || 1);
}

export function analyzeShareTransfer(params: {
  shareId: number;
  animals: AnimalItem[];
}) {
  const { shareId, animals } = params;

  const sourceAnimal = animals.find((animal) =>
    (animal.hisseler ?? []).some((share) => share.id === shareId)
  );

  const share = sourceAnimal?.hisseler.find((item) => item.id === shareId) ?? null;

  if (!sourceAnimal || !share) {
    return {
      share: null,
      sourceAnimal: null,
      targetCandidates: [] as AnimalItem[],
    };
  }

  const targetCandidates = animals.filter(
    (animal) => animal.id !== sourceAnimal.id && canTransferShareToAnimal(share, animal)
  );

  return {
    share,
    sourceAnimal,
    targetCandidates,
  };
}

export function transferShareSale(params: {
  shareId: number;
  targetAnimalId: number;
  targetSlotNos: number[];
  animals: AnimalItem[];
  customers: CustomerItem[];
  payments: PaymentRecord[];
  deliveries: DeliveryAssignment[];
}) {
  const { shareId, targetAnimalId, targetSlotNos, animals, customers, payments, deliveries } = params;

  const sourceAnimal = animals.find((animal) =>
    (animal.hisseler ?? []).some((share) => share.id === shareId)
  );
  const targetAnimal = animals.find((animal) => animal.id === targetAnimalId);
  const sourceShare = sourceAnimal?.hisseler.find((share) => share.id === shareId);

  if (!sourceAnimal || !targetAnimal || !sourceShare) {
    return {
      animals,
      customers,
      payments,
      deliveries,
      movedShare: null as ShareItem | null,
    };
  }

  const normalizedTargetSlots = uniqueSorted(targetSlotNos);
  const shareSlotCount = Math.max(1, sourceShare.hisseSayisi || getShareSlotNos(sourceShare).length || 1);
  const targetAvailable = new Set(getAvailableSlotNos(targetAnimal));

  const validSelection =
    normalizedTargetSlots.length === shareSlotCount &&
    normalizedTargetSlots.every((slotNo) => targetAvailable.has(slotNo));

  if (!validSelection) {
    return {
      animals,
      customers,
      payments,
      deliveries,
      movedShare: null as ShareItem | null,
    };
  }

  const movedShare: ShareItem = {
    ...sourceShare,
    hisseNo: buildGroupedHisseNo(targetAnimal.siraNo, normalizedTargetSlots),
    slotNos: normalizedTargetSlots,
  };

  const nextAnimals = recalcAllAnimals(
    animals.map((animal) => {
      if (animal.id === sourceAnimal.id) {
        return {
          ...animal,
          hisseler: (animal.hisseler ?? []).filter((share) => share.id !== shareId),
        };
      }

      if (animal.id === targetAnimal.id) {
        return {
          ...animal,
          hisseler: [...(animal.hisseler ?? []), movedShare].sort(
            (a, b) => getShareSortKey(a) - getShareSortKey(b)
          ),
        };
      }

      return animal;
    })
  );

  const finalTargetAnimal = nextAnimals.find((animal) => animal.id === targetAnimal.id) ?? targetAnimal;
  const nextCustomers = recalcCustomersFromAnimals(customers, nextAnimals);

  const nextPayments = payments.map((payment) =>
    payment.shareId === shareId
      ? {
          ...payment,
          animalId: finalTargetAnimal.id,
          hayvanKupeNo: getDisplayKupeNo(finalTargetAnimal),
          hayvanSiparisNo: getDisplaySiparisNo(finalTargetAnimal),
          hisseNo: movedShare.hisseNo,
        }
      : payment
  );

  const nextDeliveries = deliveries.map((item) =>
    item.shareId === shareId
      ? {
          ...item,
          animalId: finalTargetAnimal.id,
          shareNo: movedShare.hisseNo,
        }
      : item
  );

  return {
    animals: nextAnimals,
    customers: nextCustomers,
    payments: nextPayments,
    deliveries: nextDeliveries,
    movedShare,
  };
}
